import type { components } from '../../lib/api/schema.gen'

type LineItem = components['schemas']['LineItem']
type CartLinePresentation = components['schemas']['CartLinePresentation']
type PricingMode = NonNullable<CartLinePresentation['pricingProjection']>['mode']
type CartView = components['schemas']['CartView']

/**
 * The three cart-line callout states the design spec (components.md "Chip /
 * pill / tag", "Price display (fx-price)") calls for: auto-priced / quote-
 * required / locked-to-RFQ.
 */
export type CartLineReadiness = 'priced' | 'quote-required' | 'locked'

/** The real 3-state pricing mode → the UI readiness callout (UID-76). */
const READINESS_BY_MODE: Readonly<Record<PricingMode, CartLineReadiness>> = {
  auto_priced: 'priced',
  quote_required: 'quote-required',
  locked_to_rfq: 'locked',
}

/**
 * Derive the UI readiness callout for a cart line.
 *
 * PRIMARY PATH (UID-76): read the REAL 3-state pricing signal —
 * `Sourceable.pricingProjection.mode` (`auto_priced` / `quote_required` /
 * `locked_to_rfq`, ADR-0030) — off the line's server-derived
 * {@link CartLinePresentation} when the BFF supplies it, mapping it to the
 * callout. The cart response now echoes this projection (and a `dfmFail` flag)
 * back on each `linePresentation`, so the screen reads the truth instead of
 * inferring it. This closes the defect where a DFM-fail line and a benign
 * not-yet-quoted line rendered identically.
 *
 * FALLBACK PATH (back-compat): when no presentation is supplied, or an older
 * row / feed doesn't populate `pricingProjection`, fall back to the original
 * approximation from the FROZEN LineItem fields alone (`status` + `sourceQuoteId`,
 * ADR-0004) — never asserted by the client, only read:
 *  - `status === 'pending'` reads as "quote-required" — the line needs an RFQ
 *    before it can price.
 *  - `status === 'ready'` with a `sourceQuoteId` reads as "locked" — the
 *    price/promise came from an accepted Quote at a specific quantity, so the
 *    line's quantity is not freely editable here without invalidating it (DM§9).
 *  - `status === 'ready'` with no `sourceQuoteId` reads as "priced" — a
 *    catalog/parametric auto-price, freely editable.
 * The fallback collapses the DFM-fail vs. plain-pending distinction; use
 * {@link isDfmFail} (populated only on the presentation path) when that matters.
 */
export function deriveLineReadiness(
  line: LineItem,
  presentation?: CartLinePresentation,
): CartLineReadiness {
  // A REORDERED line (added by reordering a placed order — carries `sourceOrderId`) that came back
  // READY is a fresh re-buy: it replays the order's frozen price so it's PRICED, and the quantity
  // stays EDITABLE — never quote-locked. This is checked FIRST, ahead of the pricing-mode and
  // accepted-quote paths, because the sourceable's mode (e.g. `locked_to_rfq`) or a carried
  // `sourceQuoteId` would otherwise force `locked` and disable the qty. Locking applies only to a
  // line still bound to an ACTIVE accepted quote (a direct quote→cart add). A reordered line that
  // could NOT be repriced stays `pending` and falls through to `quote-required` below.
  if (presentation?.sourceOrderId != null && line.status !== 'pending') {
    return 'priced'
  }
  const mode = presentation?.pricingProjection?.mode
  if (mode !== undefined) {
    return READINESS_BY_MODE[mode]
  }
  if (line.status === 'pending') {
    return 'quote-required'
  }
  return line.sourceQuoteId ? 'locked' : 'priced'
}

/** True when a line's quantity may be changed from the cart screen — false
 * for `locked` lines, whose price/promise is fixed to an accepted Quote's
 * quantity (changing it here would silently invalidate that price). */
export function isQuantityEditable(line: LineItem, presentation?: CartLinePresentation): boolean {
  return deriveLineReadiness(line, presentation) !== 'locked'
}

/**
 * True when the line's resolved Sourceable FAILS DFM (Instant-Quoter
 * manufacturable=false, UID-49) — the signal that distinguishes a DFM-fail line
 * from a benign not-yet-quoted line that also reads "quote-required" (UID-76).
 * Only the server-derived presentation carries this; absent ⇒ false.
 */
export function isDfmFail(presentation?: CartLinePresentation): boolean {
  return presentation?.dfmFail === true
}

/** The result of the pre-checkout gate: whether the Cart may proceed to the
 * checkout/payment screen, and — when it may not — the single, highest-priority
 * reason to surface inline on the button. Also carries the readiness SPLIT
 * (UID-269) — how many of the cart's lines are ready to ship now vs. still
 * pending a quote — so callers can render the split explicitly rather than
 * just a boolean. */
export interface CheckoutReadiness {
  readonly canCheckout: boolean
  readonly reason?: string
  /** Lines that are `ready` — these become the order and ship now. */
  readonly readyLineCount: number
  /** Lines that are NOT `ready` (pending/quote-required) — these stay bound to
   * the cart and append to it on acceptance; they are never part of the order
   * this checkout places. */
  readonly pendingLineCount: number
}

/**
 * The pre-checkout gate for the "Continue to checkout" CTA (UID-130 follow-up,
 * reworked by UID-269 — "Checkout splits by readiness").
 *
 * Previously the CTA was enabled on `lineCount > 0` alone, so a cart with no
 * purchasable lines (or an incomplete ship plan) let the buyer walk all the way
 * to the payment screen and fill out a card, only to be rejected server-side
 * with "no items ready to order". This moves every one of those checks IN FRONT
 * of the payment step, disabling the CTA with a specific, discoverable reason.
 *
 * UID-269 reverses the prior "fully-purchasable cart required" product
 * decision: a MIXED cart (some `ready`, some pending/quote-required) is now
 * EXPECTED and must be checkout-able — the server already settles only the
 * ready lines at placement and leaves the pending ones in the cart to append
 * on acceptance (see the placement code's comment on this), so blocking here
 * on a mixed cart was stricter than the server ever needed. Checkout is only
 * truly blocked when there is NOTHING to order.
 *
 * Blocks (first unmet reason wins) when:
 *  1. the cart is empty;
 *  2. there is not a single `ready` line — nothing can be placed yet (every
 *     line still needs a quote), so there is nothing for "Continue to
 *     checkout" to do;
 *  3. no ship-to address is selected;
 *  4. a `ship_together` plan has no delivery option chosen (a `ship_as_ready`
 *     plan carries no order-level option, so it has nothing to require here) —
 *     UNLESS the caller passes `{ requireDeliveryOption: false }` (UID-430:
 *     the quote-generated modal's own "Proceed to checkout" reuses this same
 *     verdict, but a delivery option is a cart-level concern unrelated to the
 *     quote that was just successfully generated — the cart page's own
 *     "Continue to checkout" button keeps the default `true` and still gates
 *     on it).
 *
 * A mixed cart (≥1 ready line + ≥1 pending line) passes every one of these
 * gates — `pendingLineCount` is exposed purely for callers to render the
 * "N ship now / M stay in your cart" split copy, never to block.
 */
export function resolveCheckoutReadiness(
  cart: CartView,
  options?: { readonly requireDeliveryOption?: boolean },
): CheckoutReadiness {
  const { requireDeliveryOption = true } = options ?? {}
  const { lines, summary, shipPlan } = cart
  const readyLineCount = lines.filter((line) => line.status === 'ready').length
  const pendingLineCount = lines.length - readyLineCount

  if (lines.length === 0) {
    return {
      canCheckout: false,
      reason: 'Add at least one item before checking out.',
      readyLineCount,
      pendingLineCount,
    }
  }
  if (readyLineCount === 0) {
    return {
      canCheckout: false,
      // NB: intentionally points at the ONLY action wired from the cart today —
      // "Remove from cart" (CartLineRow). There is no cart→quote/RFQ flow yet
      // (the "Generate quote" CTA is a deliberate not-yet-wired stub), so the copy
      // must not instruct the buyer to "request a quote" they can't actually start.
      reason:
        'These items still need a quote before they can be purchased. They’ll stay in your cart until they’re ready.',
      readyLineCount,
      pendingLineCount,
    }
  }
  if (!summary.shipToAddressId) {
    return {
      canCheckout: false,
      reason: 'Select a ship-to address to continue.',
      readyLineCount,
      pendingLineCount,
    }
  }
  if (
    requireDeliveryOption &&
    shipPlan?.mode === 'ship_together' &&
    !shipPlan.selectedDeliveryOptionId
  ) {
    return {
      canCheckout: false,
      reason: 'Choose a delivery option to continue.',
      readyLineCount,
      pendingLineCount,
    }
  }
  return { canCheckout: true, readyLineCount, pendingLineCount }
}

/**
 * Buyer-facing copy for the readiness split (UID-269, design refresh
 * 2026-08-23 §7 screen 8 / §1: "ready lines become an order and ship now;
 * pending (quote) lines stay bound to the cart and append on acceptance").
 * Returns `undefined` when there is no split to announce — an all-ready cart
 * (nothing pending) or, defensively, an all-pending cart (nothing ships now,
 * and such a cart cannot check out anyway).
 */
export function describeCheckoutSplit(
  readyLineCount: number,
  pendingLineCount: number,
): string | undefined {
  if (pendingLineCount === 0 || readyLineCount === 0) return undefined
  const readyLabel = `${readyLineCount} ${readyLineCount === 1 ? 'item ships' : 'items ship'} now`
  const pendingLabel = `${pendingLineCount} ${
    pendingLineCount === 1 ? 'item stays' : 'items stay'
  } in your cart`
  return `${readyLabel} · ${pendingLabel}`
}
